/**
 * ReportsOverview — сводный список отчётов по всем проектам.
 *
 * Отличается от хранилища (`ReportsArchive`) адресатом вопроса. В хранилище
 * приходят с вопросом «что было у ЭТОГО проекта в марте», сюда — с вопросом
 * «что вообще собралось и где горит». Поэтому строка называет проект и
 * несёт вердикт: список читают, чтобы решить, какой отчёт открывать, а
 * решают это по фразе про кабинет, а не по числу.
 *
 * Два режима показа переключает сегментированная радиокнопка. Сплошной
 * список отвечает на «что нового», разложенный по проектам — на «что там у
 * клиента». Порядок записей задаёт вызывающий код, страница его не трогает:
 * группировка сохраняет свежесть и внутри группы, и между группами.
 *
 * Вёрстка — дом отчёта: полоса документа, карточки на приглушённом
 * `surface1`, строки склеены швом.
 *
 * @format
 */

import {useMemo, useState} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Button} from 'ui/components/atoms/Button';
import {ChipMUI} from 'ui/components/atoms/ChipMUI';
import {Spinner} from 'ui/components/atoms/Spinner';
import {NoticeCard} from 'ui/components/composition/NoticeCard';
import {PageHeader} from 'ui/components/composition/PageHeader';
import {Row} from 'ui/components/layout/Row';
import {Stack} from 'ui/components/layout/Stack';
import {spacingPx} from 'ui/components/layout/spacing';
import {ChipSegment, SegmentedControl} from 'ui/components/molecules/SegmentedControl';
import {AppHeader} from 'ui/components/organisms/AppHeader';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {SplitCard} from '../ExecutiveReport/components/SplitCard';
import {MEASURE_WIDE, PAGE_MAX_WIDTH, pluralize} from '../ExecutiveReport/helpers';
import {OverviewRow} from './components/OverviewRow';
import {REPORTS, groupByCompany} from './helpers';
import {MOCK_OVERVIEW} from './mocks';
import type {ReportsGrouping, ReportsOverviewData} from './types';

// NBSP в JSX-тексте запрещён линтером — типографированный текст держим
// строками.
const PAGE_TITLE = 'Управленческие отчёты';
const PAGE_LEAD =
  'Отчёт собирается по завершившемуся периоду и показывает качество ведения рекламного кабинета.';
const EMPTY_TITLE = 'Отчётов пока нет';
const EMPTY_TEXT =
  'Первый отчёт появится здесь после того, как система соберёт его за завершившийся период.';
const ERROR_TITLE = 'Список не загрузился';
const ERROR_TEXT =
  'Связь с сервером оборвалась. Данные не потеряны — попробуйте ещё раз.';
const LOADING_TEXT = 'Загружаем список…';
const RETRY_LABEL = 'Повторить';
const MORE_LABEL = 'Показать ещё';

const GROUPING_LABEL: Record<ReportsGrouping, string> = {
  none: 'Без группировки',
  company: 'По проектам',
};

export interface ReportsOverviewProps {
  /** Содержимое списка. По умолчанию — демонстрационный набор. */
  data?: ReportsOverviewData;
  /** Режим показа при первом открытии. Дальше его меняет читатель. */
  initialGrouping?: ReportsGrouping;
  /** Список едет. Показывается, пока строк ещё нет. */
  loading?: boolean;
  /** Запрос сорвался: вместо списка — плашка с кнопкой повтора. */
  error?: boolean;
  /** Повторить запрос. Без него у плашки ошибки нет кнопки. */
  onRetry?: () => void;
  /** Есть ли что догружать. Управляет кнопкой «Показать ещё». */
  hasMore?: boolean;
  /** Догрузить следующую страницу. */
  onLoadMore?: () => void;
  /** Открыть отчёт. Без него строки остаются нечитаемыми ссылками. */
  onOpenReport?: (id: string) => void;
  /** Клик по логотипу в шапке приложения. */
  onLogoClick?: () => void;
}

const Page = styled(Box)(({theme}) => ({
  backgroundColor: theme.palette.background.default,
  minHeight: '100vh',
  color: surfaceVar('onSurface'),
}));

const Content = styled(Stack)({
  maxWidth: PAGE_MAX_WIDTH,
  marginLeft: 'auto',
  marginRight: 'auto',
  paddingLeft: spacingPx('l'),
  paddingRight: spacingPx('l'),
  paddingTop: spacingPx('xl') * 2,
  paddingBottom: spacingPx('xl') * 2,
  // Тот же удвоенный шаг, что разводит этажи отчёта и хранилища.
  gap: spacingPx('xl') * 2,
});

const Lead = styled(Box)({
  ...text('body.l'),
  color: surfaceVar('onSurfaceLight'),
  maxWidth: MEASURE_WIDE,
});

/** Имя проекта над группой: заголовок раздела, а не подпись строки. */
const GroupTitle = styled(Box)({
  ...text('heading.m'),
  color: surfaceVar('onSurface'),
});

const Muted = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceMuted'),
});

export function ReportsOverview({
  data = MOCK_OVERVIEW,
  initialGrouping = 'none',
  loading = false,
  error = false,
  onRetry,
  hasMore = false,
  onLoadMore,
  onOpenReport,
  onLogoClick,
}: ReportsOverviewProps) {
  const [grouping, setGrouping] = useState<ReportsGrouping>(initialGrouping);

  const groups = useMemo(() => groupByCompany(data.reports), [data.reports]);

  const showList = data.reports.length > 0;
  const isEmpty = !loading && !error && !showList;

  return (
    <Page>
      <AppHeader variant='empty' onLogoClick={onLogoClick} />

      <Content>
        <Stack gap='m'>
          <PageHeader title={PAGE_TITLE} />
          <Lead>{PAGE_LEAD}</Lead>
        </Stack>

        {error && (
          <NoticeCard
            tone='bad'
            title={ERROR_TITLE}
            description={ERROR_TEXT}
            action={
              onRetry != null && (
                <Button variant='outline' color='primary' onClick={onRetry}>
                  {RETRY_LABEL}
                </Button>
              )
            }
          />
        )}

        {isEmpty && <NoticeCard tone='primary' title={EMPTY_TITLE} description={EMPTY_TEXT} />}

        {loading && !showList && (
          <Row gap='s' alignY='center'>
            <Spinner label={null} />
            <Muted>{LOADING_TEXT}</Muted>
          </Row>
        )}

        {showList && (
          <Stack gap='l'>
            {/* Переключатель стоит над списком, а не в шапке страницы: он
                правит список, а не страницу, и должен читаться как его
                настройка. */}
            <Row justify='start'>
              <SegmentedControl
                mode='radio'
                size='s'
                value={grouping}
                onChange={next => setGrouping(next as ReportsGrouping)}
              >
                <ChipSegment value='none'>{GROUPING_LABEL.none}</ChipSegment>
                <ChipSegment value='company'>{GROUPING_LABEL.company}</ChipSegment>
              </SegmentedControl>
            </Row>

            {grouping === 'none' ? (
              <SplitCard direction='column' reflowAt='never'>
                {data.reports.map(report => (
                  <OverviewRow key={report.id} report={report} onOpen={onOpenReport} />
                ))}
              </SplitCard>
            ) : (
              // Группы разведены сильнее, чем строки внутри них: иначе
              // заголовок проекта читается как ещё одна строка списка.
              <Stack gap='xl'>
                {groups.map(group => (
                  <Stack key={group.companyId} gap='s'>
                    <Row gap='s' alignY='center' wrap='wrap'>
                      <GroupTitle>{group.companyName}</GroupTitle>
                      <ChipMUI mode='badge' color='neutral' variant='outline' size='s'>
                        {pluralize(group.reports.length, REPORTS)}
                      </ChipMUI>
                    </Row>
                    <SplitCard direction='column' reflowAt='never'>
                      {group.reports.map(report => (
                        <OverviewRow
                          key={report.id}
                          report={report}
                          showCompany={false}
                          onOpen={onOpenReport}
                        />
                      ))}
                    </SplitCard>
                  </Stack>
                ))}
              </Stack>
            )}

            {hasMore && onLoadMore != null && (
              <Row justify='center'>
                <Button
                  variant='outline'
                  color='primary'
                  disabled={loading}
                  loading={loading ? 'left' : false}
                  onClick={onLoadMore}
                >
                  {MORE_LABEL}
                </Button>
              </Row>
            )}
          </Stack>
        )}
      </Content>
    </Page>
  );
}
